import { jsPlumb } from "jsplumb";
import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Skeleton } from "@chakra-ui/react";
import { useSidebar } from "../../../providers/Sidebar";
import { Extraction, ExtractionDataItem } from "../../../api/types";
import useGetExtractionImages from "../../../api/useGetExtractionImages";
import { typedSxMap } from "../../../utils/typedSxMap";
import { breakpoints } from "./utils/breakpoints";
import { getLeftAdjustment } from "./utils/getLeftAdjustment";
import { shouldUseLargeStubs } from "./utils/shouldUseLargeStubs";
import PDFTableFieldViewer from "./PDFTableFieldViewer";
import PDFViewer from "./PDFViewer";
import ReviewRowDetailPDF from "./ReviewRowDetailPDF";

const styles = typedSxMap({
  container: {
    display: "flex",
    flex: 1,
    height: "calc(100vh - 215px - var(--m))",
    overflowY: "auto",
    overflowX: "hidden",
    [breakpoints.md]: {
      height: "calc(100vh - 167px - var(--m))",
    },
    "& iframe": {
      flex: 3,
      width: "100%",
      height: "100%",
    },
  },
});

type PDFDataProps = {
  extraction?: Extraction;
};

const jsPlumbConfig = {
  paintStyle: { stroke: "var(--blue-light-600)", strokeWidth: 2 },
  anchors: ["Right", "Left"],
  endpoints: [
    ["Blank", {}],
    ["Dot", { cssClass: "pdfDataEndpoint" }],
  ],
};

const connectorConfig = {
  stub: 20,
  gap: [0, 100],
  cornerRadius: 0,
  alwaysRespectStubs: true,
};

export default function PDFData({ extraction }: PDFDataProps) {
  const [data, setData] = useState<{ [key: string]: ExtractionDataItem }>(extraction?.data || {});
  // const dataItems = Object.keys(extraction?.data || {}).map((key) => ({ ...extraction?.data[key], key } as ExtractionDataItem)) || [];
  const { data: images, isLoading, refetch } = useGetExtractionImages(extraction?.id ?? "");
  const [selectedDataItem, setSelectedDataItem] = useState<ExtractionDataItem | null>();
  const [selectedTemplateKey, setSelectedTemplateKey] = useState<string | null>();
  const jsPlumbInstanceRef = useRef<any>(null);
  const { navSize } = useSidebar();

  const refreshConnection = useCallback(() => {
    if (!jsPlumbInstanceRef.current || !selectedDataItem || !selectedTemplateKey) {
      return;
    }
    const sourceId = `item-${selectedDataItem.page_element?.id}`;
    const targetId = `details-${selectedDataItem.page_element?.id}-${selectedTemplateKey}`;
    const useLargerStubs = shouldUseLargeStubs(sourceId, targetId, navSize);

    jsPlumbInstanceRef.current.deleteEveryConnection();
    jsPlumbInstanceRef.current.reset();

    jsPlumbInstanceRef.current.connect({
      source: sourceId,
      target: targetId,
      connector: ["Straight", useLargerStubs ? { ...connectorConfig, stub: 40 } : connectorConfig],
      ...jsPlumbConfig,
    });
  }, [selectedDataItem, selectedTemplateKey]);

  const handleUpdateDataItem = useCallback((updatedItem: ExtractionDataItem, key: string) => {
    setData((prevItems) => ({
      ...prevItems,
      [key]: updatedItem,
    }));
    if (jsPlumbInstanceRef.current) {
      jsPlumbInstanceRef.current.repaintEverything();
    }
  }, []);

  const handleScroll = useCallback(() => {
    if (jsPlumbInstanceRef.current) {
      jsPlumbInstanceRef.current.repaintEverything();
    }
  }, []);

  // Smoothly (and hack-ily) update the line size if the sidebar is opened or closed
  useEffect(() => {
    if (jsPlumbInstanceRef.current) {
      const intervalId = setInterval(() => {
        jsPlumbInstanceRef.current.repaintEverything();
      }, 10);
      setTimeout(() => {
        clearInterval(intervalId);
      }, 500);
    }
  }, [navSize]);

  useEffect(() => {
    refreshConnection();
  }, [selectedDataItem, refreshConnection]);

  useEffect(() => {
    const instance = jsPlumb.getInstance({
      Connector: ["Straight", connectorConfig],
      ...jsPlumbConfig,
    });
    jsPlumbInstanceRef.current = instance;
    jsPlumbInstanceRef.current = instance;

    const handleResize = () => {
      if (jsPlumbInstanceRef.current) {
        jsPlumbInstanceRef.current.repaintEverything();
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      instance.deleteEveryConnection();
      instance.deleteEveryEndpoint();
      instance.reset();
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <>
      <Box sx={styles.container} tabIndex={0} id="jsPlumbContainer" onScroll={handleScroll}>
        {isLoading && <Skeleton height="100%" width="100%" position="absolute" top={0} zIndex={-1} />}
        <Box>
          {images && Object.values(images)[0] && (
            <PDFViewer
              data={data}
              selectedDataItem={selectedDataItem}
              setSelectedDataItem={(item: ExtractionDataItem | null, key: string) => {
                setSelectedTemplateKey(key);
                setSelectedDataItem(item);
              }}
              pdfImageUrl={Object.values(images)[0].url}
              onScroll={handleScroll}></PDFViewer>
          )}

          {selectedTemplateKey && selectedDataItem && (
            <PDFTableFieldViewer
              selectedDataItem={selectedDataItem || ({} as any)}
              template={extraction?.template || []}
              selectedTemplateKey={selectedTemplateKey || ""}
              extractionId={extraction?.id}
              onUpdateDataItem={handleUpdateDataItem}
              isEditable={extraction?.status === "review"}
            />
          )}
        </Box>
        <ReviewRowDetailPDF
          setSelectedDataItem={setSelectedDataItem}
          selectedDataItem={selectedDataItem}
          data={data}
          extractionId={extraction?.id}
          isReadOnly={extraction?.status !== "review"}
          template={extraction?.template || []}
          onScroll={handleScroll}
          selectedTemplateKey={selectedTemplateKey}
          setSelectedTemplateKey={setSelectedTemplateKey}
          onUpdateDataItem={handleUpdateDataItem}
          leftAdjustment={getLeftAdjustment(navSize)}
          topAdjustment={"-147px"}
        />
      </Box>
    </>
  );
}
